import { Component, EventEmitter, Output, Input, ViewChild, AfterViewInit } from '@angular/core';

import moment from 'moment';

const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
import { CalendarOptions, FullCalendarComponent } from '@fullcalendar/angular';
import engb from '@fullcalendar/core/locales/en-gb';
import { Calendar, Tooltip } from 'primeng';
import { EventSourceInput } from '@fullcalendar/core';
import { SupplierAvailabilityStateEnum } from '../../../interfaces/supplier-schedule';

export interface DayColor {
  date: Date | string;
  bgColor: string;
}

@Component({
  selector: 'app-full-calendar',
  templateUrl: './app-full-calendar.component.html',
  styleUrls: ['./app-full-calendar.component.scss'],
})
export class AppFullCalendarComponent {
  @ViewChild('calendar') calendarComponent: FullCalendarComponent;

  calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,dayGridWeek',
    },
    height: 'auto',
    timeZone: 'UTC',
    locale: engb,
    editable: false,
    buttonText: {
      today: 'Today',
      month: 'Month',
      week: 'Week',
      day: 'Day',
    },
    views: {
      agenda: {
        eventLimit: 2,
      },
    },
    firstDay: 1,
    fixedWeekCount: false,
    selectable: true,
    businessHours: [
      {
        startTime: '08:00', // 8am
        endTime: '18:00', // 6pm
      },
    ],
    slotDuration: '00:15:00',
    slotLabelInterval: '01:00:00',
    events: this.eventData,
    eventColor: '#2c3e50',
    titleFormat: { year: 'numeric', month: 'short', day: 'numeric' },
    eventDurationEditable: false,
    dayMaxEventRows: true,
    moreLinkClick: 'popover',
    dateClick: this.handleDateClick.bind(this),
    eventClick: this.eventClick.bind(this),
    eventDragStart: this.eventDragStart.bind(this),
    eventDragStop: this.eventDragStart.bind(this),
    unselect: this.unselect.bind(this),
    select: this.select.bind(this),
    viewDidMount: this.calendarCreated.bind(this),
    datesSet: this.visibleDatesChanged.bind(this),
    eventDidMount: function (info) {
      console.log('EVENT MOUNTED');
      $(info.el).attr('pTooltip', info.event.extendedProps.description);
      $(info.el).attr('tooltipPosition', 'top');
    },
    eventContent: (arg) => {
      if (arg.event.extendedProps.type == SupplierAvailabilityStateEnum.doNotDisturb) {
        return {
          html: '<i class="fa fa-minus-circle"></i> Do Not Disturb',
        };
      }
      return arg.event.title;
    },
    dayCellDidMount: this.handleDayRender.bind(this),
    selectAllow: (selectInfo) => {
      const calendarApi = this.calendarComponent.getApi();

      let startInterval = calendarApi.view.currentStart;
      let endInterval = calendarApi.view.currentEnd;

      if (
        moment(selectInfo.start).startOf('day').isSameOrAfter(moment.utc().startOf('day')) &&
        moment(selectInfo.start)
          .startOf('day')
          .isSameOrAfter(moment(startInterval).startOf('day')) &&
        moment(selectInfo.end).endOf('day').isSameOrBefore(moment(endInterval).endOf('day'))
      ) {
        return true;
      }
      return false;
    },
  };

  calendarId = '#full-calendar';

  private _calendarName: string = 'full-calendar';
  @Input('calendarName')
  set calendarName(value: string) {
    this._calendarName = value;
    this.calendarId = `#${value}`;
  }
  get calendarName(): string {
    return this._calendarName;
  }

  private _eventData: any;
  @Input('eventData')
  set eventData(value: EventSourceInput) {
    this._eventData = value;
    this.rerenderCalendar();
  }
  get eventData(): EventSourceInput {
    return this._eventData;
  }

  @Output() calendarEventChanged: EventEmitter<any>;
  @Output() calendarViewChanged: EventEmitter<any>;
  @Output() calendarDateClicked: EventEmitter<any>;
  @Output() calendarDayHeaderSelected: EventEmitter<any>;
  @Output() calendarDayClicked: EventEmitter<any>;
  @Output() calendarVisibleDatesChanged: EventEmitter<any>;

  private _dayColors: DayColor[];
  @Input('dayColors')
  set dayColors(value: DayColor[]) {
    if (value != this._dayColors) {
      this._dayColors = value;
      //this.rerenderCalendar();
      this.rebuildCalendar();

      console.log('DAY COLORS: ', value);
    }
  }

  get dayColors(): DayColor[] {
    return this._dayColors;
  }

  constructor() {
    this.calendarEventChanged = new EventEmitter<any>();
    this.calendarViewChanged = new EventEmitter<any>();
    this.calendarDateClicked = new EventEmitter<any>();
    this.calendarDayHeaderSelected = new EventEmitter<any>();
    this.calendarDayClicked = new EventEmitter<any>();
    this.calendarVisibleDatesChanged = new EventEmitter<any>();
  }

  handleDateClick(arg) {
    this.calendarDayClicked.emit(arg);
  }

  handleDayRender(args) {
    const date = new Date(args.date.toDateString());

    if (!this.dayColors) return;

    // check if we have an event with a bgColor for this date
    const dc = this.dayColors.find((x) => moment(x.date).isSame(moment(date)));

    if (!dc) {
      return;
    }

    args.el.style.backgroundColor = dc.bgColor; // Set the color for the specific date
  }

  eventClick(calendarEvent, jsEvent, activeView) {
    this.calendarEventChanged.emit(calendarEvent);
  }

  eventDragStart(timeSheetEntry, jsEvent, ui, activeView) {}

  eventDragStop(timeSheetEntry, jsEvent, ui, activeView) {}

  select(start, end, jsEvent, view) {
    this.calendarDateClicked.emit([start, end]);
  }

  unselect(jsEvent, view) {}

  dayHeaderClicked(dow) {
    this.calendarDayHeaderSelected.emit(dow);
  }

  updateCalendarEvent(event: any) {
    this.rerenderCalendar();
  }

  updateCalendarEvents(events: any[]) {
    this.rerenderCalendar();
  }

  setRecurringDays(dayAvailability: any[]) {
    if (dayAvailability) {
      dayAvailability.map((x) => {
        let cssClass = x.available ? 'available' : 'unavailable';
        let dayName = weekDays[x.day].substr(0, 3).toLowerCase();
        $('.fc-day-header')
          .filter('.fc-' + dayName)
          .removeClass('available unavailable')
          .addClass(cssClass);
      });
    }
  }

  calendarCreated({ view }) {
    this.calendarViewChanged.emit(view);
  }

  private rerenderCalendar() {
    this.calendarOptions.events = this.eventData;

    this.calendarComponent?.getApi()?.render();
  }

  private rebuildCalendar() {
    this.calendarComponent?.getApi()?.destroy();
    this.calendarComponent?.getApi()?.render();
  }

  visibleDatesChanged(calendarEvent): void {
    this.calendarVisibleDatesChanged.emit(calendarEvent);
  }
}
